import { Box, Code, Heading, Text, VStack } from "@chakra-ui/react";
import { Element } from "react-scroll";
import CodeBlock from "./CodeBlock";
import DocsTable from "./DocsTable";

export default function ForwardGeocodeDocs() {
  return (
    <VStack align="stretch" spacing={10} w="full">
      <Element name="baseurl">
        <Box>
        <Heading as="h2">Base URL</Heading>
        <Text as="p" fontSize="lg" mt={5}>
          <Code wordBreak="break-all">https://api.potterstech.com/v1/</Code>
        </Text>
        </Box>
      </Element>

      <Element name="endpoint">
        <Box>
        <Heading as="h2">Endpoint</Heading>
        <Text as="p" fontSize="lg" mt={5}>
          <Code>GET /geocode/forward</Code>
        </Text>
        <Text as="p" fontSize="lg" mt={5}>
          <Text as="span" fontWeight="bold" textDecoration="none">
            Full URL:
          </Text>{" "}
          <Code wordBreak="break-all">https://api.potterstech.com/v1/geocode/forward</Code>
        </Text>
        </Box>
      </Element>

      <Element name="authentication">
        <Box>
        <Heading as="h2">Authentication</Heading>
        <Text as="p" fontSize="lg" mt={5}>
          <Code>X-Api-Key: &lt;YOUR_API_KEY&gt;</Code>
        </Text>
        </Box>
      </Element>

      <Element name="headers">
        <Box>
        <Heading as="h2">Headers</Heading>
      <DocsTable
        headers={["Header", "Required", "Description"]}
        rows={[
          ["Content-Type", "yes", "application/json"],
          ["X-Api-Key", "yes", "Your assigned API key"],
        ]}
      />
        </Box>
      </Element>

      <Element name="requestparameters">
        <Box>
        <Heading as="h2" mb={5}>Request Parameters</Heading>
        <Text as="b" mt={5}>
          Required
        </Text>
        <DocsTable
          headers={["Parameter", "Type", "Description"]}
          rows={[["address", "string", "Human-readable address to geocode"]]}
        />

        <Text as="b" mt={8}>
          Optional
        </Text>
        <DocsTable
          headers={["Parameter", "Type", "Description"]}
          rows={[
            ["components", "string", "Component filtering, for example country:in"],
            ["bounds", "string", "Geographic bounding box to bias results"],
            ["language", "string", "Response language. Default: en"],
          ]}
        />
        </Box>
      </Element>

      <Element name="requestexamples">
        <Box>
        <Heading as="h2" mb={5}>Request Examples</Heading>
        <Text as="b" mt={5}>
          Simple Request
        </Text>
        <CodeBlock containerProps={{ mt: 8 }}>
        {`GET /geocode/forward?address=NTR%20Gardens%2C%20Khairatabad%2C%20Hyderabad%2C%20Telangana%2C%20India`}
        </CodeBlock>

        <Text as="b" mt={8}>
          With Components Filter
        </Text>
        <CodeBlock containerProps={{ mt: 8 }}>
        {`GET /geocode/forward?address=NTR%20Gardens%2C%20Khairatabad%2C%20Hyderabad%2C%20Telangana%2C%20India&components=country:in`}
        </CodeBlock>
        </Box>
      </Element>

      <Element name="responsebody">
        <Box>
        <Heading as="h2">Response Body</Heading>
        <CodeBlock containerProps={{ mt: 8 }}>
        {`{
  "results": [
    {
      "place_id": "95609768637121397521971935917962",
      "address_components": [
        {
          "long_name": "India",
          "short_name": "",
          "types": ["COUNTRY"]
        }
        // ...
      ],
      "formatted_address": "NTR Gardens, Khairatabad, Telangana, India",
      "geometry": {
        "location": { "lat": 17.412732, "lng": 78.468738 },
        "location_type": null,
        "viewport": null
      },
      "plus_code": { "compound_code": "", "global_code": "" },
      "types": ["POI_NAME"]
    }
    // ...
  ]
}`}
        </CodeBlock>
        <Text as="p" fontSize="lg" mt={5}>
          <Code>...</Code> indicates additional matching results of the same
          structure.
        </Text>
        </Box>
      </Element>

      <Element name="responsefields">
        <Box>
        <Heading as="h2" mb={5}>Response Field Reference</Heading>
        <Text as="b" mt={5}>
          results[]
        </Text>
        <DocsTable
          headers={["Field", "Type", "Description"]}
          rows={[
            ["place_id", "string", "Unique identifier for the place"],
            ["formatted_address", "string", "Human-readable address"],
            ["types", "array", "Classification of the place"],
            ["address_components", "array", "Structured address breakdown"],
            ["geometry", "object", "Geographic information"],
            ["plus_code", "object", "Plus code details (if available)"],
          ]}
        />

        <Text as="b" mt={8}>
          address_components[]
        </Text>
        <DocsTable
          headers={["Field", "Type", "Description"]}
          rows={[
            ["long_name", "string", "Full component name"],
            ["short_name", "string", "Abbreviated name (may be empty)"],
            ["types", "array", "Component category (COUNTRY, STATE, CITY, etc.)"],
          ]}
        />

        <Text as="b" mt={8}>
          geometry
        </Text>
        <DocsTable
          headers={["Field", "Type", "Description"]}
          rows={[
            ["location.lat", "number", "Latitude"],
            ["location.lng", "number", "Longitude"],
            ["location_type", "string/null", "Reserved"],
            ["viewport", "object/null", "Reserved"],
          ]}
        />
        </Box>
      </Element>

      <Element name="examplerequests">
        <Box>
        <Heading as="h2" mb={5}>Example Requests</Heading>
        <Text as="b" mt={5}>
          cURL
        </Text>
        <CodeBlock containerProps={{ mt: 8 }} variant="dark">
        {`curl -X GET \\
  'https://api.potterstech.com/v1/geocode/forward?address=NTR%20Gardens%2C%20Khairatabad%2C%20Hyderabad%2C%20Telangana%2C%20India' \\
  -H 'Content-Type: application/json' \\
  -H 'X-Api-Key: YOUR_API_KEY'`}
      </CodeBlock>
        <Text as="b" mt={8}>
          JavaScript
        </Text>
        <CodeBlock containerProps={{ mt: 8 }} variant="dark">
        {`const res = await fetch(
  "https://api.potterstech.com/v1/geocode/forward?address=NTR%20Gardens%2C%20Khairatabad%2C%20Hyderabad%2C%20Telangana%2C%20India",
  {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-Api-Key": process.env.POTTERS_API_KEY
    }
  }
);

console.log(await res.json());`}
      </CodeBlock>
        <Text as="b" mt={8}>
          Python
        </Text>
        <CodeBlock containerProps={{ mt: 8 }} variant="dark">
        {`import os, requests

url = "https://api.potterstech.com/v1/geocode/forward"
params = {
  "address": "NTR Gardens, Khairatabad, Hyderabad, Telangana, India"
}
headers = {
  "Content-Type": "application/json",
  "X-Api-Key": os.environ["POTTERS_API_KEY"]
}

resp = requests.get(url, headers=headers, params=params)
print(resp.json())`}
      </CodeBlock>
        </Box>
      </Element>

      <Element name="statuscodes">
        <Box>
        <Heading as="h2">Status Codes</Heading>
        <DocsTable
          headers={["Code", "Description"]}
          rows={[
            ["200", "Geocoding successful"],
            ["400", "Invalid or missing address"],
            ["401", "Invalid or missing API key"],
            ["403", "Access forbidden"],
            ["429", "Rate limit exceeded"],
            ["5xx", "Server error"],
          ]}
        />
        </Box>
      </Element>

      <Element name="notes">
        <Box>
        <Heading as="h2">Notes</Heading>
        <Text as="p" fontSize="lg" mt={5}>
          <ul>
            <li>Results are ordered by relevance to the input address.</li>
            <li>Use <Code>components</Code> to restrict results to a specific
            country or region.</li>
            <li><Code>formatted_address</Code> is suitable for display.</li>
            <li>Use <Code>geometry.location</Code> for mapping or spatial
            calculations.</li>
            <li>Forward geocoding pairs naturally with reverse geocoding for
            bidirectional address handling.</li>
          </ul>
        </Text>
        </Box>
      </Element>
    </VStack>
  );
}

